/**
 * SidebarFolder - Generic folder components for sidebar resource groups
 *
 * Reusable components for displaying folders (resource groups) with:
 * - Expand/collapse functionality
 * - Drag & drop support
 * - Custom color support
 * - Consistent styling with other sidebar elements
 */

import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronRight, FaPlus, FaGripVertical, FaThumbtack } from "react-icons/fa";

// ============================================================================
// Types
// ============================================================================

export interface SidebarFolderHeaderProps {
  /** Folder display name */
  name: string;
  /** Icon to display (typically FaFolder/FaFolderOpen) */
  icon: React.ReactNode;
  /** Number of items in the folder */
  count: number;
  /** Whether the folder is expanded */
  isExpanded: boolean;
  /** Whether the folder is pinned */
  isPinned?: boolean;
  /** Callback when clicked */
  onClick: () => void;
  /** Callback when double-clicked */
  onDoubleClick?: () => void;
  /** Callback when right-clicked */
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Whether an item is being dragged over this folder */
  isDragOver?: boolean;
  /** Custom color for the folder icon */
  color?: string;
  /** Whether to show a drag handle */
  showDragHandle?: boolean;
  /** Callback when drag starts on the handle */
  onDragStart?: (e: React.DragEvent) => void;
  /** Callback when drag ends */
  onDragEnd?: () => void;
  /** Hover actions to show on the right */
  hoverActions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

const SidebarFolderHeader: React.FC<SidebarFolderHeaderProps> = ({
  name,
  icon,
  count,
  isExpanded,
  isPinned = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  isDragOver = false,
  color,
  showDragHandle = false,
  onDragStart,
  onDragEnd,
  hoverActions,
  className = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const hasCustomColor = !!color;

  return (
    <motion.div
      whileHover={{ x: 2 }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--arc-sidebar-accent-glow)] transition-all text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] ${
        isDragOver ? "bg-[var(--arc-accent)]/20 ring-2 ring-[var(--arc-accent)] ring-inset" : ""
      } ${className}`}
    >
      {/* Drag Handle */}
      {showDragHandle && (
        <span
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="flex-shrink-0 p-1 -ml-1 cursor-grab active:cursor-grabbing text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] hover:bg-[var(--arc-sidebar-accent-glow)] rounded transition-colors"
          title="Glisser pour réorganiser"
          onClick={(e) => e.stopPropagation()}
        >
          <FaGripVertical size={10} />
        </span>
      )}

      {/* Expand/Collapse Icon */}
      <motion.span
        animate={{ rotate: isExpanded ? 90 : 0 }}
        transition={{ duration: 0.2 }}
        className="w-4 flex-shrink-0"
      >
        <FaChevronRight size={10} />
      </motion.span>

      {/* Folder Icon with optional color - shows plus when dragging over */}
      <span
        className={`relative ${!hasCustomColor ? "arc-sidebar-icon-active" : ""}`}
        style={hasCustomColor ? { color } : undefined}
      >
        {icon}
        {isDragOver && (
          <FaPlus
            size={8}
            className="absolute -right-1 -bottom-1 text-[var(--arc-accent)] bg-[var(--arc-sidebar-bg)] rounded-full"
          />
        )}
      </span>

      {/* Folder Name */}
      <span className="flex-1 text-[var(--arc-sidebar-text-primary)] text-sm font-medium truncate">
        {name}
      </span>

      {/* Hover Actions */}
      <AnimatePresence>
        {isHovered && hoverActions && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1"
          >
            {hoverActions}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pin indicator + child count badge */}
      {!isHovered && (
        <span className="flex items-center gap-1.5 text-xs text-[var(--arc-sidebar-text-secondary)]">
          {isPinned && <FaThumbtack size={10} className="arc-sidebar-pin-icon" />}
          {count > 0 && <span>{count}</span>}
        </span>
      )}
    </motion.div>
  );
};

SidebarFolderHeader.displayName = "SidebarFolderHeader";

export default SidebarFolderHeader;

// ============================================================================
// Folder Content Wrapper
// ============================================================================

export interface SidebarFolderContentProps {
  /** Whether the folder is expanded */
  isExpanded: boolean;
  /** Child content */
  children: React.ReactNode;
  /** Message to show when empty */
  emptyMessage?: string;
  /** Whether the folder is empty */
  isEmpty?: boolean;
  /** Custom color for the border */
  color?: string;
}

/**
 * SidebarFolderContent - Animated wrapper for folder content with indentation
 */
export const SidebarFolderContent: React.FC<SidebarFolderContentProps> = ({
  isExpanded,
  children,
  emptyMessage = "Dossier vide",
  isEmpty = false,
  color,
}) => {
  const hasCustomColor = !!color;

  return (
    <AnimatePresence>
      {isExpanded && !isEmpty && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div
            className="ml-6 border-l border-[var(--arc-sidebar-border-accent)] pl-2 py-1 space-y-0.5"
            style={hasCustomColor ? { borderColor: `${color}40` } : undefined}
          >
            {children}
          </div>
        </motion.div>
      )}

      {isExpanded && isEmpty && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="ml-6 pl-2 py-2 text-[var(--arc-sidebar-text-secondary)]">
            <span className="text-xs italic">{emptyMessage}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

SidebarFolderContent.displayName = "SidebarFolderContent";

// ============================================================================
// Folder Action Button
// ============================================================================

export interface SidebarFolderActionProps {
  /** Click handler */
  onClick: (e: React.MouseEvent) => void;
  /** Tooltip text */
  title: string;
  /** Icon to display */
  icon: React.ReactNode;
  /** Visual variant */
  variant?: "default" | "pin";
}

/**
 * SidebarFolderAction - Action button for folder hover state
 */
export const SidebarFolderAction: React.FC<SidebarFolderActionProps> = ({
  onClick,
  title,
  icon,
  variant = "default",
}) => {
  const baseClasses = "p-1 rounded transition-colors";
  const variantClasses =
    variant === "pin"
      ? "hover:bg-[var(--arc-sidebar-accent-glow)] arc-sidebar-pin-icon"
      : "hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]";

  return (
    <button onClick={onClick} className={`${baseClasses} ${variantClasses}`} title={title}>
      {icon}
    </button>
  );
};

SidebarFolderAction.displayName = "SidebarFolderAction";
